/**
 * Incoming P2P call overlay (web).
 *
 * Shown when a call arrives while the user is NOT already on a call — the
 * ordinary "someone is calling you" case. Deaf users can't hear a ring, so
 * while this is visible the tab title flashes and a browser notification
 * fires (see deafAccessibleAlert). The card itself shows who's calling and
 * gives one-tap Answer / Decline.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { clearIncomingP2PCall } from '../actions';
import { startCallAttention } from '../deafAccessibleAlert';
function callRef(data) {
    if (!data || typeof data !== 'object')
        return {};
    const record = data;
    return {
        callId: typeof record.callId === 'string' ? record.callId : undefined,
        invitationId: typeof record.invitationId === 'string' ? record.invitationId : undefined
    };
}
const IncomingCallOverlay = () => {
    const dispatch = useDispatch();
    const incoming = useSelector((state) => state['features/call-management']?.incomingP2PCall);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const [unavailable, setUnavailable] = useState(false);
    const incomingRef = useRef(incoming);
    incomingRef.current = incoming;
    const stopAttentionRef = useRef(null);
    const pendingRef = useRef(false);
    const attemptedRef = useRef(false);
    useEffect(() => {
        incomingRef.current = incoming;
        pendingRef.current = false;
        attemptedRef.current = false;
        setPending(false);
        setError('');
        setUnavailable(false);
        if (!incoming) {
            return undefined;
        }
        const stop = startCallAttention({
            callerName: incoming.callerName || 'Malka contact',
            body: 'Incoming video call — open Malka to answer.'
        });
        stopAttentionRef.current = stop;
        const matches = (data) => data.callId === incoming.callId
            && (!data.invitationId || data.invitationId === incoming.invitationId);
        const clear = (data) => {
            if (matches(data))
                dispatch(clearIncomingP2PCall());
        };
        // Cancel/end payloads are typed loosely on the service; read only the ids.
        const gone = (data) => clear(callRef(data));
        const failed = (data) => {
            if (!incoming.conferenceInvitation || !matches(data) || data.invitationId !== incoming.invitationId)
                return;
            pendingRef.current = false;
            setPending(false);
            setUnavailable(data.code !== 'RESPONSE_TIMEOUT' && data.code !== 'INVITATION_FAILED');
            setError(data.message || 'Your answer could not be confirmed. Please try again.');
        };
        // A direct-call Answer is a request, not an admission. When the server
        // refuses it (call already ended, not a participant) the refusal used
        // to vanish into the console while the card kept ringing.
        const refused = (data) => {
            if (incoming.conferenceInvitation || !pendingRef.current || data.retrying)
                return;
            pendingRef.current = false;
            setPending(false);
            setUnavailable(data.code === 'NOT_FOUND' || data.code === 'FORBIDDEN');
            setError(data.message || 'Your answer could not be confirmed. Please try again.');
        };
        queueService.on('p2pJoinRoom', clear);
        queueService.on('callEnded', clear);
        // The caller hung up or lost the network while this was ringing.
        // Mobile clears its sheet on these; the web card used to ring on.
        queueService.on('p2pCallCancelled', gone);
        queueService.on('p2pCallEnded', gone);
        queueService.on('conferenceInvitationUpdate', clear);
        queueService.on('conferenceInvitationError', failed);
        queueService.on('error', refused);
        // Every ring has a window (the server's still-active reminder is 30 s);
        // a card that outlives it is a ghost.
        const expires = incoming.expiresAt ? Date.parse(incoming.expiresAt) : Date.now() + 30000;
        const timeout = setTimeout(() => {
            if (!attemptedRef.current) {
                stop();
                dispatch(clearIncomingP2PCall());
            }
        }, Math.max(0, Math.min(30000, expires - Date.now())));
        return () => {
            incomingRef.current = null;
            stopAttentionRef.current = null;
            stop();
            clearTimeout(timeout);
            queueService.off('p2pJoinRoom', clear);
            queueService.off('callEnded', clear);
            queueService.off('p2pCallCancelled', gone);
            queueService.off('p2pCallEnded', gone);
            queueService.off('conferenceInvitationUpdate', clear);
            queueService.off('conferenceInvitationError', failed);
            queueService.off('error', refused);
        };
    }, [incoming?.callId, incoming?.invitationId, dispatch]);
    if (!incoming) {
        return null;
    }
    const callerName = incoming.callerName || 'Malka contact';
    const handleAnswer = () => {
        if (pendingRef.current || unavailable || incomingRef.current !== incoming)
            return;
        stopAttentionRef.current?.();
        setError('');
        if (!incoming.inboundVrs) {
            // Stay on screen as "Joining…" until p2p_join_room admits us (or
            // the server refuses); inbound VRS answers navigate immediately.
            attemptedRef.current = true;
            pendingRef.current = true;
            setPending(true);
        }
        try {
            if (incoming.inboundVrs) {
                // A hearing caller dialed this client's VRS number: there is no
                // p2p call to accept — join the queued room directly through the
                // same path a p2p_join_room event takes. Sending p2p_accept here
                // would carry a queue requestId and a phone number where the
                // server expects call/user ids.
                queueService.announceLocalRoomJoin({
                    callId: incoming.callId,
                    callerName: incoming.callerName,
                    clientStartWithVideoMuted: incoming.clientStartWithVideoMuted,
                    inboundVrs: true,
                    roomName: incoming.roomName
                });
            }
            else {
                queueService.acceptP2PCall({
                    callId: incoming.callId,
                    callerId: incoming.callerId,
                    conferenceInvitation: incoming.conferenceInvitation,
                    invitationId: incoming.invitationId,
                    roomName: incoming.roomName
                });
            }
            if (incoming.inboundVrs)
                dispatch(clearIncomingP2PCall());
        }
        catch {
            pendingRef.current = false;
            setPending(false);
            setError('Your answer could not be sent. Check your connection and try again.');
        }
    };
    const handleDecline = () => {
        if (incomingRef.current !== incoming)
            return;
        // Inbound VRS rings have no p2p call to decline — the queued request
        // times out server-side; just dismiss the overlay.
        try {
            if (!incoming.inboundVrs) {
                queueService.declineP2PCall({
                    callId: incoming.callId,
                    callerId: incoming.callerId,
                    conferenceInvitation: incoming.conferenceInvitation,
                    invitationId: incoming.invitationId
                });
            }
            dispatch(clearIncomingP2PCall());
        }
        catch {
            setError('Your response could not be sent. Check your connection and try again.');
        }
    };
    return (React.createElement("div", { "aria-label": `Incoming call from ${callerName}`, className: 'call-waiting-overlay', role: 'alertdialog' },
        React.createElement("div", { className: 'call-waiting-card' },
            React.createElement("div", { className: 'call-waiting-icon incoming-pulse' },
                React.createElement("i", { className: 'icon-phone-incoming' })),
            React.createElement("h3", null, "Incoming Call"),
            React.createElement("p", { className: 'call-waiting-caller' }, callerName),
            React.createElement("p", { "aria-live": 'polite', className: 'call-waiting-status' }, error || (pending ? 'Joining…' : 'is calling you')),
            React.createElement("div", { className: 'call-waiting-actions' },
                React.createElement("button", { className: 'call-waiting-btn reject', onClick: handleDecline },
                    React.createElement("i", { className: 'icon-phone-hangup' }),
                    React.createElement("span", null, "Decline")),
                React.createElement("button", { "aria-busy": pending, disabled: pending || unavailable, className: 'call-waiting-btn accept', onClick: handleAnswer },
                    React.createElement("i", { className: 'icon-phone' }),
                    React.createElement("span", null, pending ? 'Joining…' : 'Answer'))))));
};
export default IncomingCallOverlay;
